import React, { useState } from "react";
import { X, MessageSquare, Plus, FileText, Clock, User, CheckCircle2 } from "lucide-react";

export default function OrderNotesModal({
  isOpen,
  onClose,
  po,
  currentUser,
  onSaveNote
}) {
  const [newNoteText, setNewNoteText] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  if (!isOpen || !po) return null;

  // Aggregate all notes and lifecycle logs across the entire PO
  const allNotes = [];

  // 1. PO-level logs
  if (po.logs && Array.isArray(po.logs)) {
    po.logs.forEach((log) => {
      allNotes.push({
        text: log.text || log.note || log.message || "",
        author: log.author || log.user || "Staff",
        timestamp: log.timestamp || log.date || po.createdAt || "",
        source: log.source || "PO General Note",
        type: log.type || "General"
      });
    });
  }

  // 2. PO-level string note
  if (po.notes && typeof po.notes === "string" && po.notes.trim() !== "") {
    if (!allNotes.some(n => n.text === po.notes)) {
      allNotes.push({
        text: po.notes,
        author: po.author || po.userName || "System",
        timestamp: po.date || po.createdAt || "",
        source: "PO Note",
        type: "General"
      });
    }
  }

  // 3. Update Bid reason
  if (po.updateReason && typeof po.updateReason === "string" && po.updateReason.trim() !== "") {
    allNotes.push({
      text: po.updateReason,
      author: po.updatedBy || "Staff",
      timestamp: po.updatedAt || po.date || "",
      source: "Bid Update Justification",
      type: "Update Bid"
    });
  }

  // 4. Change Request history
  if (po.changeHistory && Array.isArray(po.changeHistory)) {
    po.changeHistory.forEach((ch, idx) => {
      const changesText = Array.isArray(ch.changes) ? ch.changes.join("; ") : (ch.summary || "");
      const fullText = ch.reason ? `${ch.reason}${changesText ? ` — [Changes: ${changesText}]` : ""}` : changesText;
      if (fullText) {
        allNotes.push({
          text: fullText,
          author: ch.changedBy || ch.author || "Staff",
          timestamp: ch.changedAt || ch.timestamp || "",
          source: `Change Request #${ch.version || idx + 1}`,
          type: "Change Request"
        });
      }
    });
  }

  // 5. Line item and vendor split notes
  if (po.items && Array.isArray(po.items)) {
    po.items.forEach((item) => {
      const lineLabel = `Line ${item.lineNumber || "Item"}: ${item.description || item.name || "Item"}`;

      if (item.logs && Array.isArray(item.logs)) {
        item.logs.forEach((log) => {
          allNotes.push({
            text: log.text || log.note || "",
            author: log.author || log.user || "Staff",
            timestamp: log.timestamp || log.date || "",
            source: lineLabel,
            type: log.type || "Line Item"
          });
        });
      }

      if (item.notes && typeof item.notes === "string" && item.notes.trim() !== "") {
        if (!allNotes.some(n => n.text === item.notes && n.source === lineLabel)) {
          allNotes.push({
            text: item.notes,
            author: "System",
            timestamp: po.date || "",
            source: lineLabel,
            type: "Line Item Note"
          });
        }
      }

      if (item.vendors && Array.isArray(item.vendors)) {
        item.vendors.forEach((v) => {
          const vendorLabel = `${lineLabel} (Vendor: ${v.vendor || "Split"})`;
          if (v.logs && Array.isArray(v.logs)) {
            v.logs.forEach((log) => {
              allNotes.push({
                text: log.text || log.note || "",
                author: log.author || log.user || "Staff",
                timestamp: log.timestamp || log.date || "",
                source: vendorLabel,
                type: log.type || "Vendor Split"
              });
            });
          }
        });
      }
    });
  }

  // Filter out empty notes
  const validNotes = allNotes.filter(n => n.text && n.text.trim() !== "");

  // Sort notes newest-first
  validNotes.sort((a, b) => {
    const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    return timeB - timeA;
  });

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNoteText.trim() || isSaving) return;

    try {
      setIsSaving(true);
      if (onSaveNote) {
        await onSaveNote(po.id, newNoteText.trim());
      }
      setNewNoteText("");
      setSuccessMsg("Note appended successfully!");
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      console.error("Error saving note:", err);
      alert("Failed to save note: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "—";
    try {
      const d = new Date(ts);
      if (isNaN(d.getTime())) return String(ts);
      return d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric"
      }) + " " + d.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit"
      });
    } catch {
      return String(ts);
    }
  };

  const getSourceBadgeStyle = (type) => {
    switch (type) {
      case "Change Request":
        return { background: "rgba(245, 158, 11, 0.15)", color: "#f59e0b", border: "1px solid rgba(245, 158, 11, 0.3)" };
      case "Update Bid":
        return { background: "rgba(168, 85, 247, 0.15)", color: "#a855f7", border: "1px solid rgba(168, 85, 247, 0.3)" };
      case "Vendor Split":
        return { background: "rgba(16, 185, 129, 0.15)", color: "#10b981", border: "1px solid rgba(16, 185, 129, 0.3)" };
      case "Line Item":
      case "Line Item Note":
        return { background: "rgba(59, 130, 246, 0.15)", color: "#3b82f6", border: "1px solid rgba(59, 130, 246, 0.3)" };
      default:
        return { background: "rgba(148, 163, 184, 0.15)", color: "var(--text-secondary)", border: "1px solid rgba(148, 163, 184, 0.3)" };
    }
  };

  return (
    <div
      className="modal-backdrop"
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1100,
        padding: "1rem"
      }}
    >
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "680px",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "var(--bg-card)",
          border: "1px solid var(--border-color)",
          borderRadius: "8px",
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.4)",
          overflow: "hidden"
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "1.25rem 1.5rem",
            borderBottom: "1px solid var(--border-color)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <MessageSquare size={18} style={{ color: "var(--primary)" }} />
              <h3 style={{ margin: 0, fontSize: "1.1rem", color: "var(--text-primary)" }}>
                PO Notes & History Log
              </h3>
              <span className="badge badge-primary" style={{ fontSize: "0.75rem" }}>
                {po.poNumber}
              </span>
              <span
                style={{
                  fontSize: "0.75rem",
                  padding: "0.15rem 0.5rem",
                  borderRadius: "10px",
                  background: "rgba(255, 255, 255, 0.08)",
                  color: "var(--text-secondary)"
                }}
              >
                {validNotes.length} Note{validNotes.length === 1 ? "" : "s"}
              </span>
            </div>
            <p style={{ margin: "0.25rem 0 0 0", fontSize: "0.85rem", color: "var(--text-secondary)" }}>
              Client: <strong style={{ color: "var(--text-primary)" }}>{po.clientName || "Unknown"}</strong>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn-icon"
            style={{
              background: "transparent",
              border: "none",
              color: "var(--text-secondary)",
              cursor: "pointer",
              padding: "0.35rem"
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Notes Scrollable Body */}
        <div
          style={{
            padding: "1.25rem 1.5rem",
            overflowY: "auto",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "0.85rem"
          }}
        >
          {validNotes.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "3rem 1rem",
                color: "var(--text-muted)"
              }}
            >
              <FileText size={36} style={{ margin: "0 auto 0.75rem", opacity: 0.4 }} />
              <p style={{ margin: 0, fontSize: "0.95rem" }}>
                No notes or activity logs recorded for this PO.
              </p>
            </div>
          ) : (
            validNotes.map((note, idx) => (
              <div
                key={idx}
                style={{
                  padding: "0.85rem 1rem",
                  backgroundColor: "rgba(255, 255, 255, 0.02)",
                  border: "1px solid var(--border-color)",
                  borderRadius: "6px"
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    gap: "0.5rem",
                    marginBottom: "0.5rem"
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.75rem",
                      padding: "0.15rem 0.5rem",
                      borderRadius: "4px",
                      fontWeight: 600,
                      ...getSourceBadgeStyle(note.type)
                    }}
                  >
                    {note.source}
                  </span>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      fontSize: "0.75rem",
                      color: "var(--text-muted)"
                    }}
                  >
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                      <User size={12} />
                      {note.author}
                    </span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                      <Clock size={12} />
                      {formatTimestamp(note.timestamp)}
                    </span>
                  </div>
                </div>
                <div
                  style={{
                    fontSize: "0.88rem",
                    color: "var(--text-primary)",
                    lineHeight: "1.45",
                    whiteSpace: "pre-wrap"
                  }}
                >
                  {note.text}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Append Note Footer */}
        <div
          style={{
            padding: "1rem 1.5rem",
            borderTop: "1px solid var(--border-color)",
            backgroundColor: "rgba(255, 255, 255, 0.01)"
          }}
        >
          {successMsg && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
                color: "#10b981",
                fontSize: "0.82rem",
                marginBottom: "0.5rem"
              }}
            >
              <CheckCircle2 size={14} />
              {successMsg}
            </div>
          )}
          <form onSubmit={handleAddNote}>
            <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}>
                <textarea
                  className="form-input"
                  placeholder="Append a note or log to this PO..."
                  value={newNoteText}
                  onChange={(e) => setNewNoteText(e.target.value)}
                  rows={2}
                  style={{
                    margin: 0,
                    resize: "none",
                    fontSize: "0.85rem",
                    width: "100%"
                  }}
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!newNoteText.trim() || isSaving}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.35rem",
                  padding: "0.55rem 0.9rem",
                  minHeight: "auto",
                  whiteSpace: "nowrap"
                }}
              >
                <Plus size={15} />
                {isSaving ? "Saving..." : "Add Note"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
